import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const activeCat = params.get("category") || "All";
  const sort = params.get("sort") || "name";

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/products/categories").then((r) => setCats(["All", ...r.data]));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (activeCat !== "All") list = list.filter((p) => p.category === activeCat);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((p) => {
        const haystacks = [
          p.name,
          p.sku,
          p.short_description,
          p.description || "",
          p.category,
          ...(p.variants || []).map((v) => v.label),
        ];
        return haystacks.some((s) => (s || "").toLowerCase().includes(needle));
      });
    }
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    else list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, activeCat, q, sort]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "All") next.set(k, v);
    else next.delete(k);
    setParams(next);
  };

  return (
    <div data-testid="shop-page" className="min-h-screen">
      <section className="border-b-[3px] border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="font-mono-ui text-xs text-[#ff1493]">— THE CATALOG</div>
          <h1 className="font-display text-5xl md:text-6xl leading-none mt-3">Shop peptides<span className="text-[#ff1493]">.</span></h1>
          <p className="mt-3 text-sm text-black/70 max-w-2xl">Browse the complete UK Peptide Research catalog &mdash; every batch independently verified for purity.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          <aside className="space-y-6">
            <div className="border-[3px] border-black bg-white p-5 shadow-brut-sm">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4" strokeWidth={2.5} />
                <div className="font-mono-ui text-xs">SEARCH</div>
                {q.trim() && (
                  <button
                    onClick={() => setQ("")}
                    data-testid="shop-search-clear"
                    className="ml-auto font-mono-ui text-[9px] font-bold px-2 py-0.5 border-[2px] border-black hover:bg-black hover:text-white"
                  >
                    CLEAR
                  </button>
                )}
              </div>
              <input
                data-testid="shop-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="BPC-157, 40mg, metabolic…"
                className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs focus:outline-none focus:bg-[#ff1493]/10"
              />
            </div>
            <div className="border-[3px] border-black bg-white p-5 shadow-brut-sm">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4" strokeWidth={2.5} />
                <div className="font-mono-ui text-xs">CATEGORY</div>
              </div>
              <div className="flex flex-col gap-2">
                {cats.map((c) => (
                  <button
                    key={c}
                    data-testid={`cat-${c.toLowerCase().replace(/\s|&/g, "-")}`}
                    onClick={() => setParam("category", c)}
                    className={`text-left px-3 py-2 border-[3px] border-black font-mono-ui text-[11px] font-bold transition-colors ${activeCat === c ? "bg-[#ff1493] text-white" : "bg-white hover:bg-[#00d4ff]"}`}
                  >
                    {c.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div className="border-[3px] border-black bg-white p-5 shadow-brut-sm">
              <div className="font-mono-ui text-xs mb-3">SORT</div>
              <select
                data-testid="shop-sort"
                value={sort}
                onChange={(e) => setParam("sort", e.target.value)}
                className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs bg-white"
              >
                <option value="name">Name A→Z</option>
                <option value="price-asc">Price low → high</option>
                <option value="price-desc">Price high → low</option>
              </select>
            </div>
          </aside>

          <div>
            <div className="font-mono-ui text-xs text-black/60 mb-4">
              {filtered.length} / {products.length} PRODUCTS · {q.trim() ? `SEARCH: "${q.trim().toUpperCase()}"` : activeCat.toUpperCase()}
            </div>
            {filtered.length === 0 ? (
              <div className="border-[3px] border-black p-10 text-center font-mono-ui text-xs">
                NO PRODUCTS MATCH "{q.trim() || activeCat}". TRY ANOTHER SEARCH OR CATEGORY.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
